import { Strong, Text } from '@clay-run/glaze/text';

import { useGongFlowStepsForced } from '../../EnrollQueue/gong-flow-queries';
import { useBatchStepBuffer } from '../../EnrollQueue/useBatchStepBuffer';
import type { IdentityKey, PlayKey } from '../../EnrollQueue/state/staged-play-config';
import { StepRows } from './StepRows';

/**
 * "Flow step defaults" — the BATCH rung of a Gong play's Bulk Editor
 * (plan 2026-09-30-002, U3 step 4; D21 composition, D29 copy). The Glaze
 * replacement for `EnrollQueue/BulkStepDefaultsEditor`, mounted by
 * `PlaySection` under `BaseCopyEditor` whenever `group.ui?.has_step_overrides`.
 *
 * The rep types default step copy ONCE and every not-individually-touched
 * lead in the section reflects it live — no Apply button, no snapshot. That
 * live-follow is not implemented here: this component writes into
 * `state/staged-play-config`'s `stepCopy`, and each follower row reads that
 * same staged record back as its `batchStepCopy` rung. This file is a
 * heading, a buffer and a `<div onBlur>`.
 *
 * Everything load-bearing is in `EnrollQueue/useBatchStepBuffer` — the
 * 200ms-per-step typing buffer, the staged read channel, the timer/unmount
 * flushes and the mount-time `reconcileStagedStepCopy` prune (E15). V1's
 * `BulkStepDefaultsEditor` consumes the same hook, so the two editors cannot
 * drift on any of it (D6). Read that hook's header before changing anything
 * here; two consequences for this file specifically:
 *
 *  1. **The `<div onBlur={flushAllPending}>` wrapper is the BLUR flush** and
 *     is caller-side by design: React's synthetic `blur` is backed by the
 *     native `focusout`, which bubbles, so one wrapper catches a blur from
 *     any step's Subject input or body editor with no `onBlur` prop on
 *     `StepRows`/`StepRow`. This is what makes a Send click land the
 *     keystroke the rep typed 50ms earlier (E44/E45) — the click blurs the
 *     field before the handler reads the staged store.
 *  2. **Do NOT add a `reconcileStagedStepCopy` effect here.** The hook runs
 *     it exactly once per consumer, from its own `liveSteps` argument.
 *
 * The batch rung has no persisted state and therefore no save errors and no
 * `overrideFor`: nothing is ever PUT against a batch entry. Materialization
 * happens per lead, inside `EnrollQueueContext.saveStepOverrides`.
 */

/**
 * A `seedKey`/aria-namespace ingredient LOCAL to this mount — `StepRow`
 * composes `RichHtmlField`'s per-step seedKey out of it and nothing else. It
 * is never a key in any store shared with real items and never touches a save
 * path, so it cannot collide with an enrollment item id. Deliberately its own
 * constant rather than an import from `BulkStepDefaultsEditor`: that value is
 * private to that component's mount for exactly the same reason, and sharing
 * one would imply a shared namespace that does not exist.
 */
const BATCH_PSEUDO_ITEM_ID = -1;

export interface BulkEditorProps {
  /** `EnrollQueueContext.baseCopyDraftStore.identity` — the identity every staged-play-config read/write in this section uses. */
  identity: IdentityKey | null;
  /** `playConfigKey(playTypeId, playInstanceRef)` — this group's play key. */
  playKey: PlayKey;
  /** The group's Gong flow id (`PlaySection`'s `gongFlowId`, non-null by the `ui.has_step_overrides` mount gate). */
  flowId: string;
  /** `group.ui.body_format`, threaded straight through to the rows. */
  bodyFormat?: 'plain' | 'rich';
  /** `Read-only while impersonating` — every field inert. */
  readOnly: boolean;
  /** `sending || groupSendPending || groupBusy` — editing batch copy mid-send would race the save phase (V1 parity). */
  busy?: boolean;
}

export function BulkEditor({ identity, playKey, flowId, bodyFormat, readOnly, busy = false }: BulkEditorProps) {
  // Only for the buffer's mount-time prune — `StepRows` runs the same hook for
  // its own render and react-query hands both the one cache entry.
  const stepsQuery = useGongFlowStepsForced(flowId);
  const {
    localDraftFor,
    subscribeStep,
    saveErrorFor,
    handleChangeBatchStep,
    handleResetBatchStep,
    flushAllPending,
  } = useBatchStepBuffer({ identity, playKey, liveSteps: stepsQuery.data?.steps });

  return (
    <div className="grid gap-1.5">
      {/* D29/D21 copy, verbatim. */}
      <div>
        <Strong className="text-xs">Flow step defaults</Strong>{' '}
        <Text size="xs" variant="tertiary">
          — applied to every lead in this batch (optional)
        </Text>
      </div>
      {/* See point 1 in the header — this wrapper IS the blur flush. */}
      <div onBlur={flushAllPending}>
        <StepRows
          scope="batch"
          flowId={flowId}
          itemId={BATCH_PSEUDO_ITEM_ID}
          bodyFormat={bodyFormat}
          disabled={readOnly || busy}
          localDraftFor={localDraftFor}
          saveErrorFor={saveErrorFor}
          subscribeStep={subscribeStep}
          onEditStepOverride={handleChangeBatchStep}
          onResetStepOverride={handleResetBatchStep}
        />
      </div>
    </div>
  );
}
